import { atom, useAtomValue, useSetAtom } from 'jotai'
import type { WritableAtom } from 'jotai'
import { queryClientAtom } from 'jotai-tanstack-query'
import { selectAtom } from 'jotai/utils'
import { useCallback } from 'react'
import isEqual from 'react-fast-compare'
import { mediumAreas$ } from '~/features/facets/atoms'
import {
  type LargeArea,
  MediumArea,
  findMinimum,
  findSmall,
  isMajorGenre,
} from '~/features/facets/types'
import type { SearchRestaurantsSortOrder } from '~/lib/graphql/graphql'
import type {
  GenreCode,
  LargeAreaCode,
  MediumAreaCode,
  MinimumAreaCode,
  SmallAreaCode,
} from '~/lib/graphql/scalars'
import { useInitAtom } from '~/lib/jotai'
import { prefectureNameByCode } from '~/lib/prefecture/prefecture'
import { isPC$ } from '~/lib/ua'
import type { AdditionalCriteria } from '~/navigation'
import { eventNavigate$ } from '~/navigation/atoms'
import { cities$, genreMap$, towns$ } from '../../facets'
import type { Genre } from '../../facets'
import { draftSearchCriteria$ } from './draft'
import type { Facet } from './facet'
import { createSelectedOthers } from './facet/selected'
import { useToggleDraftIncludeClosedInventory } from './inventory'
import { currentPreferences } from './preferences'
import type { PriceCriteria } from './price'
import { toPriceLabel } from './price'
import { useSetDraftRadius } from './radius'
import { scope } from './scope'
import type { SearchCriteria } from './validate'

export const appliedSearchCriteria$ = scope.atom<SearchCriteria | undefined>(
  undefined,
  'appliedSearchCriteria$'
)

export const appliedEnabled$ = atom(false)
appliedEnabled$.debugLabel = 'appliedEnabled$'

/** navigation には時間がかかるのでページングのときは先に反映して、そちらを優先する */
export const pageLoading$ = atom<number | undefined>(undefined)
pageLoading$.debugLabel = 'pageLoading$'

export type SetSearchCriteria =
  | SearchCriteria
  | ((current: SearchCriteria | undefined) => SearchCriteria)

export type ApplyAtom = WritableAtom<
  null,
  [SetSearchCriteria] | [SetSearchCriteria, number] | [SetSearchCriteria, number, boolean],
  SearchCriteria
>

export const apply$: ApplyAtom = atom(null, (get, set, value, page = 1, _ = false) => {
  const criteria = typeof value === 'function' ? value(get(appliedSearchCriteria$)) : value
  if (get(isPC$)) {
    window.scrollTo({ top: 0, behavior: 'instant' })
  }
  // navigate が完了する前に検索条件の表示を更新したい
  // https://github.com/ikyucom/macaron-frontend/issues/3758
  set(appliedEnabled$, false)
  // navigation には時間がかかるので完了までに一時的に遷移後の page を保持する
  // atomWithQuery のキーに使っているのですぐに利用したい
  set(pageLoading$, page)
  set(appliedSearchCriteria$, criteria)
  set(draftSearchCriteria$, criteria)
  return criteria
})

export const applyAndNavigate$: ApplyAtom = atom(null, (_, set, value, page = 1, force = false) => {
  const criteria = set(apply$, value, page, force)
  set(removeLazyFetchCache$)
  set(eventNavigate$, {
    type: 'search',
    ...criteria,
    page,
    force,
  })
  return criteria
})

// 循環参照を避けるためにこっちで定義
// queryKey については lazy-fetch.tsx 参照
const removeLazyFetchCache$ = atom(null, (get) => {
  const queryClient = get(queryClientAtom)
  queryClient.removeQueries({ queryKey: ['reservable-calendar'] })
  queryClient.removeQueries({ queryKey: ['reservable-times'] })
})

export function useRemoveLazyFetchCache() {
  return useSetAtom(removeLazyFetchCache$)
}

/**
 * 検索条件を適用して Remix navigate する
 */
export function useApplySearchCriteria() {
  return useSetAtom(applyAndNavigate$)
}

export const applyDraft$ = atom<null, [] | [SearchCriteria | boolean], void>(
  null,
  (get, set, update = {}) => {
    const draft = get(draftSearchCriteria$)
    const current = get(appliedSearchCriteria$)
    const next: SearchCriteria = update === true ? (draft ?? {}) : { ...draft, ...update }
    const force = update === true
    if (force || !isEqual(current, next)) {
      set(applyAndNavigate$, next)
    }
  }
)

/** draft を apply する */
export function useApplyDraftSearchCriteria() {
  return useSetAtom(applyDraft$)
}

/** ページを変更して Remix navigate する */
export function useChangePage() {
  const apply = useApplySearchCriteria()
  return useCallback(
    (page: number, force?: boolean) => {
      apply((current) => current ?? {}, page, force ?? false)
    },
    [apply]
  )
}

export function useEnableAppliedSearchCriteria() {
  const set = useSetAtom(appliedEnabled$)
  return useCallback(() => {
    set(true)
  }, [set])
}

export function useAppliedSearchCriteria() {
  return useAtomValue(appliedSearchCriteria$)
}

export function useHydrateAppliedSearchCriteria(criteria: SearchCriteria | undefined) {
  useInitAtom(appliedSearchCriteria$, criteria)
}

export function useAdditionalCriteria(): AdditionalCriteria {
  const criteria = useAppliedSearchCriteria()
  return {
    childUses: criteria?.childUses,
    seatTypes: criteria?.seatTypes,
    windowSide: criteria?.windowSide,
    smokingAllowed: criteria?.smokingAllowed,
    buffet: criteria?.buffet,
    freeFlow: criteria?.freeFlow,
    sceneTagIds: criteria?.sceneTagIds,
  }
}

export function useReserveParams() {
  return {
    visitDate: useAtomValue(visitDate$),
    visitTime: useAtomValue(visitTime$),
    visitorsCount: useAtomValue(visitorsCount$),
  }
}

export function useVisitDate() {
  return useAtomValue(visitDate$)
}

export function useVisitTime() {
  return useAtomValue(visitTime$)
}

export function useVisitorsCount() {
  return useAtomValue(visitorsCount$)
}

const largeAreaCode$ = selectAtom(appliedSearchCriteria$, (c) => c?.largeAreaCode)
largeAreaCode$.debugLabel = 'largeAreaCode$'
const mediumAreaCode$ = selectAtom(appliedSearchCriteria$, (c) => c?.mediumAreaCode)
mediumAreaCode$.debugLabel = 'mediumAreaCode$'

const smallAreaCodes$ = selectAtom(appliedSearchCriteria$, (c) => c?.smallAreaCodes ?? [], isEqual)
smallAreaCodes$.debugLabel = 'smallAreaCodes$'
const minimumAreaCodes$ = selectAtom(
  appliedSearchCriteria$,
  (c) => c?.minimumAreaCodes ?? [],
  isEqual
)
minimumAreaCodes$.debugLabel = 'minimumAreaCodes$'

const appliedCityId$ = selectAtom(appliedSearchCriteria$, (c) => c?.cityId)
const appliedTownId$ = selectAtom(appliedSearchCriteria$, (c) => c?.townId)

const areaName$ = atom((get) => {
  const largeAreaCode = get(largeAreaCode$)
  const mediumAreaCode = get(mediumAreaCode$)
  if (largeAreaCode === undefined || mediumAreaCode === undefined) {
    return []
  }
  const cityId = get(appliedCityId$)
  if (cityId !== undefined) {
    const towns = get(towns$(cityId))?.data
    if (towns !== undefined) {
      const townId = get(appliedTownId$)
      const found = towns.find((t) => t.id === townId)
      if (found !== undefined) {
        return [found.name]
      }
    }
    const cities = get(cities$)?.data
    if (cities !== undefined) {
      const found = cities.find((c) => c.id === cityId)
      if (found !== undefined) {
        return [found.name]
      }
    }
  }

  return getAreaName({
    largeAreaCode,
    mediumAreaCode,
    minimumAreaCodes: get(minimumAreaCodes$),
    smallAreaCodes: get(smallAreaCodes$),
    mediumArea: get(mediumAreas$([largeAreaCode, mediumAreaCode])).data,
  })
})
areaName$.debugLabel = 'areaName$'

function getAreaName({
  largeAreaCode,
  mediumAreaCode,
  minimumAreaCodes,
  smallAreaCodes,
  mediumArea,
}: {
  largeAreaCode: LargeAreaCode
  mediumAreaCode: MediumAreaCode
  minimumAreaCodes: MinimumAreaCode[]
  smallAreaCodes: SmallAreaCode[]
  mediumArea: MediumArea | undefined
}) {
  if (mediumArea === undefined) {
    return []
  }
  if (smallAreaCodes.length === 0 && minimumAreaCodes.length === 0) {
    return [prefectureNameByCode(`${largeAreaCode}${mediumAreaCode}`) ?? '']
  }
  const smalls = smallAreaCodes
    .map((code) => findSmall(mediumArea, code))
    .filter((s) => s !== undefined)
    .map((s) =>
      // 「その他エリア」だとどこかわからないので「東京都その他エリア」のように表示
      s.code === '999' && mediumArea !== undefined
        ? { ...s, name: `${mediumArea.name}${s.name}` }
        : s
    )
  const minimums = minimumAreaCodes
    .map((code) => findMinimum(mediumArea, code))
    .filter((m) => m !== undefined)
    // 小エリアが選択されている場合は、小エリアに所属する極小エリアは表示しない
    .filter((m) => smalls.every((s) => !s.minimums.includes(m)))
  return [...smalls, ...minimums].map((a) => a.name)
}
if (import.meta.vitest) {
  const { it, describe, expect } = import.meta.vitest
  describe('getAreaName', async () => {
    const largeAreaCode = '03' as LargeAreaCode
    const mediumAreaCode = '001' as MediumAreaCode
    const areas = await import('../../facets/data/areas.json').then((m) => m.default as LargeArea[])
    const mediumArea = areas
      .find((l) => l.code === largeAreaCode)
      ?.mediums.find((m) => m.code === mediumAreaCode)
    it('極小エリアと小エリア指定なし', () => {
      expect(
        getAreaName({
          largeAreaCode,
          mediumAreaCode,
          minimumAreaCodes: [],
          smallAreaCodes: [],
          mediumArea,
        })
      ).toEqual(['東京都'])
    })
    it('小エリアを指定している場合、小エリアに所属する極小エリアを表示しない', () => {
      const smallAreaCodes = ['009'] as SmallAreaCode[]
      const minimumAreaCodes = ['0022', '0023', '0024', '0128', '0129'] as MinimumAreaCode[]
      expect(
        getAreaName({
          largeAreaCode,
          mediumAreaCode,
          minimumAreaCodes,
          smallAreaCodes,
          mediumArea,
        })
      ).toEqual(['銀座・日比谷・有楽町'])
    })
    it('極小エリアのみ指定', () => {
      const smallAreaCodes = [] as SmallAreaCode[]
      const minimumAreaCodes = ['0022', '0023'] as MinimumAreaCode[]
      expect(
        getAreaName({
          largeAreaCode,
          mediumAreaCode,
          minimumAreaCodes,
          smallAreaCodes,
          mediumArea,
        })
      ).toEqual(['銀座', '日比谷'])
    })
  })
}

export function useAreaName() {
  return useAtomValue(areaName$)
}

const selected$ = createSelectedOthers(appliedSearchCriteria$, 'preferences')

export const currentPreferences$ = atom((get) => {
  const criteria = get(appliedSearchCriteria$)
  const selected = get(selected$)
  return currentPreferences(selected, criteria)
})
currentPreferences$.debugLabel = 'currentPreferences$'

const genreNames$ = atom((get) => {
  const genreCodes = get(appliedSearchCriteria$)?.genreCodes ?? []
  const genreMap = get(genreMap$)
  return getGenreNames(genreCodes, genreMap)
})
genreNames$.debugLabel = 'genreNames$'

function getGenreNames(genreCodes: GenreCode[], genreMap: Map<string, Genre>) {
  // 条件に選択しているジャンルのうち、メジャージャンルに属するものを抽出する
  const majors = [...genreMap.values()]
    .filter(isMajorGenre)
    .filter((g) => genreCodes.includes(g.code))
  return (
    genreCodes
      // メジャージャンルが選択されている場合は、メジャージャンルに所属するマイナージャンルは表示しない
      .filter((code) => !majors.some((m) => m.minors.find((c) => c.code === code)))
      .map((code) => {
        return genreMap.get(code)?.name
      })
      .filter((n) => n !== undefined)
  )
}
if (import.meta.vitest) {
  const { it, describe, expect } = import.meta.vitest
  describe('getGenreNames', () => {
    const genreMap = new Map<string, Genre>([
      [
        '10000',
        {
          minors: [
            {
              code: '10001' as GenreCode,
              major_code: '10000' as GenreCode,
              name: 'フランス料理',
              tag_id: 46,
              display_order: 1,
            },
            {
              code: '10002' as GenreCode,
              major_code: '10000' as GenreCode,
              name: 'イタリア料理',
              tag_id: 47,
              display_order: 2,
            },
            {
              code: '10004' as GenreCode,
              major_code: '10000' as GenreCode,
              name: 'スペイン料理',
              tag_id: 48,
              display_order: 3,
            },
            {
              code: '10008' as GenreCode,
              major_code: '10000' as GenreCode,
              name: 'ステーキ・グリル料理',
              tag_id: 49,
              display_order: 4,
            },
            {
              code: '10999' as GenreCode,
              major_code: '10000' as GenreCode,
              name: '洋食その他',
              tag_id: 54,
              display_order: 5,
            },
          ],
          code: '10000' as GenreCode,
          name: '洋食',
          tag_id: 6,
          display_order: 1,
        },
      ],
      [
        '10001',
        {
          code: '10001' as GenreCode,
          major_code: '10000' as GenreCode,
          name: 'フランス料理',
          tag_id: 46,
          display_order: 1,
        },
      ],
      [
        '10002',
        {
          code: '10002' as GenreCode,
          major_code: '10000' as GenreCode,
          name: 'イタリア料理',
          tag_id: 47,
          display_order: 2,
        },
      ],
      [
        '10004',
        {
          code: '10004' as GenreCode,
          major_code: '10000' as GenreCode,
          name: 'スペイン料理',
          tag_id: 48,
          display_order: 3,
        },
      ],
      [
        '10008',
        {
          code: '10008' as GenreCode,
          major_code: '10000' as GenreCode,
          name: 'ステーキ・グリル料理',
          tag_id: 49,
          display_order: 4,
        },
      ],
      [
        '10999',
        {
          code: '10999' as GenreCode,
          major_code: '10000' as GenreCode,
          name: '洋食その他',
          tag_id: 54,
          display_order: 5,
        },
      ],
    ])
    it('何も指定しない', () => {
      const genreCodes: GenreCode[] = []
      const genreMap = new Map<string, Genre>()
      expect(getGenreNames(genreCodes, genreMap)).toEqual([])
    })
    it('マイナージャンルのみ指定', () => {
      const genreCodes: GenreCode[] = ['10001', '10002', '10004', '10008', '10999'] as GenreCode[]
      expect(getGenreNames(genreCodes, genreMap)).toEqual([
        'フランス料理',
        'イタリア料理',
        'スペイン料理',
        'ステーキ・グリル料理',
        '洋食その他',
      ])
    })
    it('メジャージャンルを指定した場合、該当ジャンルに所属するマイナージャンルは表示しない', () => {
      const genreCodes: GenreCode[] = [
        '10000',
        '10001',
        '10002',
        '10004',
        '10008',
        '10999',
      ] as GenreCode[]
      expect(getGenreNames(genreCodes, genreMap)).toEqual(['洋食'])
    })
  })
}

export function useGenreNames() {
  return useAtomValue(genreNames$)
}

const displayNames$ = atom((get) => {
  const genres = get(genreNames$)
  const areas = get(areaName$)
  const preferences = get(currentPreferences$)
  return [...areas, ...genres, ...preferences].filter((n) => n !== '')
})
displayNames$.debugLabel = 'displayNames$'

export function useDisplayNames() {
  return useAtomValue(displayNames$)
}

export const appliedMinimumAreaCodes$ = selectAtom(
  appliedSearchCriteria$,
  (c) => c?.minimumAreaCodes
)
appliedMinimumAreaCodes$.debugLabel = 'appliedMinimumAreaCodes$'

export const visitDate$ = selectAtom(appliedSearchCriteria$, (s) => s?.visitDate)
visitDate$.debugLabel = 'visitDate$'
const visitTime$ = selectAtom(appliedSearchCriteria$, (s) => s?.visitTime)
visitTime$.debugLabel = 'visitTime$'
export const visitorsCount$ = selectAtom(appliedSearchCriteria$, (s) => s?.visitorsCount)
visitorsCount$.debugLabel = 'visitorsCount$'
const freeFlow$ = selectAtom(appliedSearchCriteria$, (s) => s?.freeFlow ?? false)
freeFlow$.debugLabel = 'freeFlow$'

const sortOrder$ = selectAtom(appliedSearchCriteria$, (c) => c?.sortOrder)
sortOrder$.debugLabel = 'sortOrder$'

export function useSortOrder() {
  return useAtomValue(sortOrder$)
}

const changeSortOrder$ = atom<null, [SearchRestaurantsSortOrder], SearchCriteria>(
  null,
  (get, set, sortOrder) => {
    const applied = get(appliedSearchCriteria$)
    const result = { ...applied, sortOrder }
    return set(apply$, result)
  }
)

export function useChangeSortOrder(): (sortOrder: SearchRestaurantsSortOrder) => SearchCriteria {
  return useSetAtom(changeSortOrder$)
}

export const appliedFacets$ = createSelectedOthers(appliedSearchCriteria$, 'applied')
export function useAppliedFacets() {
  return useAtomValue(appliedFacets$)
}

const appliedFacetsCount$ = atom((get) => {
  const facetCounts = get(appliedFacets$).length
  const criteria = get(appliedSearchCriteria$)
  // 予算の上限/下限は1つの条件としてカウントする
  const priceCount =
    criteria?.lowerBoundPrice !== undefined || criteria?.upperBoundPrice !== undefined ? 1 : 0
  return facetCounts + priceCount
})
appliedFacetsCount$.debugLabel = 'appliedFacetsCount$'

export function useAppliedFacetsCount() {
  return useAtomValue(appliedFacetsCount$)
}

export function useAppliedRemove(facet: Facet) {
  const criteria = useAtomValue(appliedSearchCriteria$)
  const apply = useApplySearchCriteria()
  return useCallback(() => {
    apply(facet.remove(criteria))
  }, [apply, criteria, facet])
}

const appliedPriceLabel$ = atom((get) => toPriceLabel(get(appliedSearchCriteria$) ?? {}))
appliedPriceLabel$.debugLabel = 'appliedPriceLabel$'

export function useAppliedPriceLabel() {
  return useAtomValue(appliedPriceLabel$)
}

export function useApplyPrice() {
  const { lowerBoundPrice, upperBoundPrice, ...applied } = useAppliedSearchCriteria() ?? {}
  const apply = useApplySearchCriteria()
  return useCallback(
    (criteria: PriceCriteria) => {
      const next = { ...applied, ...criteria }
      apply(next)
    },
    [applied, apply]
  )
}

export function useApplyRadius() {
  const set = useSetDraftRadius()
  const apply = useApplySearchCriteria()
  return useCallback(
    (radius: number) => {
      const criteria = set(radius)
      apply(criteria ?? {})
    },
    [apply, set]
  )
}

export const includeClosedInventory$ = selectAtom(
  appliedSearchCriteria$,
  (c) => c?.includeClosedInventory
)
includeClosedInventory$.debugLabel = 'includeClosedInventory$'

export function useIncludeClosedInventory() {
  return useAtomValue(includeClosedInventory$)
}

export function useToggleIncludeClosedInventory() {
  const toggle = useToggleDraftIncludeClosedInventory()
  const apply = useApplySearchCriteria()
  return useCallback(() => {
    const criteria = toggle()
    apply(criteria ?? {})
  }, [apply, toggle])
}

// appliedSearchCriteria が更新される度に rssrh cookie を更新する
export const listenAppliedSearchCriteria$ = atom((get, set) => {
  const criteria = get(appliedSearchCriteria$)
  if (
    criteria === undefined ||
    criteria.largeAreaCode === undefined ||
    criteria.mediumAreaCode === undefined
  ) {
    return
  }

  // atomEffect も SSR で実行されるため、SSR では fetch しないようにする
  // 関連：https://ikyu.slack.com/archives/C4JKVQYMT/p1728014293265729
  if (typeof window !== 'undefined') {
    // rssrh の cookie を更新
    // document.cookie で設定すると、Safari の ITP により cookie がブロックされるため、
    // サーバーサイドで更新する必要がある
    // https://webkit.org/blog/8828/intelligent-tracking-prevention-2-2/
    fetch('/api/v2/cookie/rssrh', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        value: criteria,
      }),
    })
  }
})
listenAppliedSearchCriteria$.debugLabel = 'listenAppliedSearchCriteria$'
